import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { forkJoin, Observable, Subject } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { DiaryNote } from '@features/diary-notes/model/diary-note';
import { DiaryNoteService } from '@features/diary-notes/services/diary-note.service';
import {
  CommandEvent,
  commandEvent
} from '@shared/components/load/command/command.component';
import { DatesUtil } from '@shared/utilities/dates/dates.util';
import { StandardSchemaUtil } from '@shared/utilities/standard-schema-util';
import { diff } from 'deep-object-diff';

type LoadContent = {
  schema: DiaryNote.Definition;
  content: DiaryNote.Model;
};

@Component({ template: '' })
export abstract class DiaryNoteDetailsComponent implements OnInit {
  @Input() cancel: () => void = () => {};
  @Input() saved: () => void = () => {};

  abstract get title(): string;
  abstract get saveLabel(): string;
  abstract content(): Observable<DiaryNote.Model>;
  abstract get isNewDiaryNote(): boolean;

  diaryNoteId!: string;
  entityId!: string;
  load!: LoadContent;

  private loadSchema$: Observable<DiaryNote.Definition> =
    this.diaryNoteService.getDiaryNoteDefinition();

  load$!: Observable<LoadContent>;
  protected change$: Subject<any> = new Subject<any>();
  triggerValidation$ = new Subject<boolean>();

  form = this.fb.group({
    summary: [
      null as string | null,
      [Validators.required, Validators.maxLength(128)]
    ],
    details: [
      null as string | null,
      [Validators.required, Validators.maxLength(2000)]
    ],
    createdDate: [null as string | null]
  });

  update$: Observable<CommandEvent<object>> = commandEvent(
    this.change$,
    (x) => {
      const payload = this.isNewDiaryNote
        ? { ...x, createdDate: DatesUtil.localDateTime() }
        : x;
      return this.diaryNoteService.updateDiaryNote(
        this.entityId,
        this.diaryNoteId,
        payload
      );
    }
  );

  constructor(
    protected readonly diaryNoteService: DiaryNoteService,
    protected fb: FormBuilder
  ) {}

  ngOnInit(): void {
    this.load$ = forkJoin([this.loadSchema$, this.content()]).pipe(
      map(([schema, content]) => ({ schema, content })),
      tap((x) => {
        StandardSchemaUtil.attachFormGroupToSchema(this.form, x.schema);
        this.form.patchValue(x.content);
        this.load = x;
      })
    );
  }

  save() {
    if (this.form.invalid) {
      this.triggerValidation$.next(true);
    } else {
      const formValue = this.form.getRawValue();
      let delta = diff(
        this.load.content,
        formValue
      ) as Partial<DiaryNote.Model>;

      this.change$.next(delta);
    }
  }
}
